import {
  IScaffoldZoneColorResolverParams,
  IScaffoldZoneColorResolverResult,
  ScaffoldDropZone,
} from "../../../scaffold";
import { ArsSidebarItemKind } from "../../sidebar";
import { cellActionConfig } from "../cellActionConfig";
import { ArsTableCellType, ArsTableState } from "../interface";

/**
 * Highlight colour used for all ArsTable drop-zone overlays.
 * Matches the centre-quadrant colour from DEFAULT_SCAFFOLD_ZONE_COLORS.
 */
export const ARST_DROP_ZONE_HIGHLIGHT_COLOUR = "#4dabf7";
const LEFT_EDGE_ZONES = new Set<ScaffoldDropZone>([
  ScaffoldDropZone.TopLeft,
  ScaffoldDropZone.LeftEdgeTop,
  ScaffoldDropZone.LeftEdgeBottom,
  ScaffoldDropZone.BottomLeft,
]);

/**
 * Drop zones on the right edge of a cell (insert-after).
 */
const RIGHT_EDGE_ZONES = new Set<ScaffoldDropZone>([
  ScaffoldDropZone.TopRight,
  ScaffoldDropZone.RightEdgeTop,
  ScaffoldDropZone.RightEdgeBottom,
  ScaffoldDropZone.BottomRight,
]);

/**
 * Builds a `ScaffoldZoneColors` resolver for an ArsTable scaffold.
 *
 * The returned function highlights zones that have a configured drop action for the
 * evaluated cell. All matching zones within the same action group share the same
 * highlight colour and are returned together via `additionalHighlightedZones` so the
 * entire action group lights up at once.
 *
 * When `dragKind` is non-null, only zones whose `droppedDataKinds` includes that kind
 * are highlighted, suppressing zones that are incompatible with the item being dragged.
 *
 * When `suppressAllZones` is true, no zones are highlighted regardless of other parameters.
 * This is used when the dragged item is a grouping factor already present in the table,
 * preventing it from being dropped anywhere.
 *
 * For RowHeader cells, RHS actions are only resolved for the last RowHeader column and
 * LHS actions are only resolved for the first RowHeader column.
 *
 * @param state - Current ArsTable state, or null when unavailable
 * @param dragKind - Kind of the sidebar item currently being dragged, or null when no drag is active
 * @param suppressAllZones - When true, all zone highlights are suppressed; defaults to false
 * @returns A `ScaffoldZoneColors` resolver function
 */
export const buildArsTableZoneColors =
  <T>(
    state: ArsTableState<T> | null,
    dragKind: ArsSidebarItemKind | null,
    suppressAllZones = false,
  ): ((params: IScaffoldZoneColorResolverParams) => IScaffoldZoneColorResolverResult) =>
  ({ rowId, colId, zone }) => {
    const result: IScaffoldZoneColorResolverResult = { color: null };
    if (suppressAllZones) return result;
    if (!state?.scaffoldModel) return result;
    const targetCell = state.scaffoldModel.cellData?.[rowId]?.[colId];
    if (!targetCell) return result;
    const config = cellActionConfig(targetCell, state);
    const matchedAction = config.dropActions.find(
      (action) =>
        action.dropZones.has(zone) &&
        (dragKind === null || action.droppedDataKinds.includes(dragKind)),
    );
    if (!matchedAction) return result;
    const matchedZones = Array.from(matchedAction.dropZones);

    // For RowHeader cells, suppress RHS actions on non-last headers and
    // LHS actions on non-first headers to avoid misleading drop targets.
    if (
      targetCell.cellType === ArsTableCellType.RowHeader &&
      state.bodyData.rowHeaders.length > 1
    ) {
      const rowHeaderIndex = parseInt(targetCell.dataPath.replace("/rowHeaders/", ""), 10);
      const rowHeaderCount = state.bodyData.rowHeaders.length;
      const isLastRowHeader = !isNaN(rowHeaderIndex) && rowHeaderIndex === rowHeaderCount - 1;
      const isFirstRowHeader = !isNaN(rowHeaderIndex) && rowHeaderIndex === 0;
      if (!isLastRowHeader && matchedZones.some((z) => RIGHT_EDGE_ZONES.has(z))) {
        return result;
      }
      if (!isFirstRowHeader && matchedZones.some((z) => LEFT_EDGE_ZONES.has(z))) {
        return result;
      }
    }

    return {
      color: ARST_DROP_ZONE_HIGHLIGHT_COLOUR,
      additionalHighlightedZones: matchedZones,
    };
  };
